/**
 * `AW12` · Borde HTTP de la pantalla «Métricas» de F4.
 *
 * La pantalla del Management Tool habla SOLO con `apps/admin-web` por HTTP y no
 * guarda estado (`arquitectura.html` §1: «no hay proceso que operar: es un
 * bucket»). Este archivo es el ÚNICO de `metricas/` que emite `fetch` (guard ①),
 * con la forma que `contracts/analytics-api/http.ts` declara.
 *
 * ⭐ ⟦ADR-0082 · 05/10/2026 · R4-UIM1⟧ · **LAS SEIS OPERACIONES DE R4.** El
 * párrafo de arriba decía *«las dos lecturas … y las dos escrituras —`Semana`
 * (cargar/editar) y la lista de columnas visibles—»*; se conserva y ya no es lo
 * que este archivo hace. Ahora son:
 *
 *   · `GET metricas` · `GET errores` — las dos lecturas (sin cambio).
 *   · `POST semanas` · `PUT semanas/{nombre}` — cargar y editar (sin cambio).
 *   · `DELETE semanas/{nombre}` — **borrar**, que `R4-CAP1` agregó (F3 regla 8).
 *   · `GET metricas/descarga` · `GET errores/descarga` — **las dos descargas**,
 *     con la primitiva de `R4-UI0` en vez de navegar (A6 / `ADR-0386`).
 *
 * ⛔ **Y la lista de columnas visibles YA NO ESTÁ**: `R4-CAP1` quitó el endpoint
 * del contrato (F3 RT 3, la lista de errores es fija en el código). No se
 * reemplazó por una lista vacía ni fija acá — se fue la operación entera.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ① LO QUE ENTRA DEL REPO SIGUEN SIENDO SÓLO TIPOS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Misma regla que `borde-envios.ts` y `borde-exportacion.ts`: los imports de
 * `contracts/**` son `import type`. El alias `@contracts/*` vive en `tsconfig.json`
 * y NO en `vite.config.ts`, así que importar un VALOR de un contrato haría fallar
 * el build. Por eso las rutas se repiten acá como literales, **tipados contra los
 * tipos de path del contrato**: el día que una ruta cambie en
 * `contracts/analytics-api/http.ts`, este build se cae en vez de seguir hablándole
 * a un endpoint que no existe.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ② NI EL BRAZO NI EL ACTOR CRUZAN POR EL NAVEGADOR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `contracts/analytics-api/http.ts` ⟦ni-arm-ni-actor-en-el-cable⟧: no hay ruta,
 * ni query param, ni campo de cuerpo por donde el cliente elija el brazo o el
 * actor. `admin-web` los resuelve desde la sesión autenticada (`ADR-0006`). Un
 * `?brazo=` acá sería la primera petición manipulada que lee o escribe el brazo
 * equivocado en un RCT. Una auditoría que el navegador pudiera escribir sería una
 * auditoría que el auditado escribe.
 */

// Tabla de cierre de R4-SAN-UI (una fila por error de la línea base): archivo:línea · contrato · dueño · cómo quedó — completa en `pruebas/R4-SAN-UI-cierre.md`.
import type {
  AnalyticsDescargaPath,
  ErroresDescargaPath,
  ErroresPath,
  ErrorsResponseBody,
  MetricasPath,
  MetricsResponseBody,
  SemanaPath,
  SemanaRequestBody,
  SemanasPath,
} from "@contracts/analytics-api/http.ts"
import type { AnalyticsManagementApi } from "@contracts/analytics-api/index.ts"
import type { ConfiguracionInstancia } from "../configuracion.ts"
import { mensajeDeError } from "../mensaje-de-error.ts"
import { raizDelBrazo } from "../brazo-del-cable.ts"
/**
 * ⭐ `R4-UIM1` · A6 / `ADR-0386` · la descarga es LA PRIMITIVA de `R4-UI0`, no
 * una tercera copia de `fetch`+`Blob` en `metricas/`. Exportación y
 * Conversaciones consumen esta misma función.
 */
import { descargarArchivo } from "../descargar-archivo.ts"

/**
 * El paginado de las dos lecturas, DERIVADO del puerto y no importado de
 * `contracts/analytics` (el contrato de DOMINIO, que el `guard` ① prohíbe tocar).
 * `MetricsQuery` y `ErrorsQuery` son estructuralmente iguales: `page` base 1 y
 * `pageSize`.
 */
type PaginaQuery = Parameters<AnalyticsManagementApi["metrics"]>[1]

/**
 * El borde de métricas visto desde el navegador.
 *
 * Sigue la forma del contrato pero adapta las entradas que la pantalla puede
 * emitir: el `arm` y el `actor` no entran por acá — los resuelve `admin-web` de
 * la sesión (`ADR-0006`).
 */
export type BordeMetricas = {
  /** `GET metricas?pagina=N&tamano=M`. El `total` es el de la base, no `items.length`. */
  readonly metricas: (query: PaginaQuery) => Promise<MetricsResponseBody>
  /** `GET errores?pagina=N&tamano=M`. Cada fila trae TODOS los códigos del periodo. */
  readonly errores: (query: PaginaQuery) => Promise<ErrorsResponseBody>
  /**
   * ⛔ SIN `columnasVisibles` NI `guardarColumnas` (guard G2 / regla 3): `R4-CAP1`
   * quitó del contrato el endpoint de columnas (F3 RT 3). El borde NO las pide y
   * ⛔ NO inventa una lista fija ni vacía: la pantalla que las dibujaba cae por su
   * camino de error nombrando a `R4-UIM1`, su dueña. El endpoint vuelve con esa
   * etapa, no acá.
   */
  /**
   * `POST semanas`. Carga un periodo nuevo; contesta `202` sin cuerpo, o `409`
   * si el `Item` o la `Posición` ya existen en el brazo.
   *
   * ⛔ `semana.desde` y `semana.hasta` son DÍAS `aaaa-mm-dd` (texto), no
   * instantes. Ver ⟦los-dias-no-se-convierten⟧ más abajo.
   */
  readonly cargarSemana: (semana: SemanaRequestBody) => Promise<void>
  /** `PUT semanas/{nombre}`. Edita el periodo identificado por `semana.nombre`; `202`, `404` o `409`. */
  readonly editarSemana: (semana: SemanaRequestBody) => Promise<void>
  /**
   * ⭐ `R4-UIM1` · `DELETE semanas/{nombre}`. Borra el periodo; `202`
   * (`writeAccepted`) sin cuerpo.
   *
   * ⛔ **Lleva el `nombre` y NADA MÁS**, porque es lo único que el puerto recibe:
   * `contracts/analytics-api/index.ts:685` declara
   * `borrar(unit, request: SemanaDeleteRequest)` y `:328` dice que el `request`
   * trae *«el `Item` del periodo a borrar»*. Mandar la `Semana` entera sería
   * mandar tres campos que el servidor ignora y que la pantalla podría tener
   * desactualizados.
   *
   * ⛔ **El `borrar` que `CAA2` prohibía entra con `R4-CAP1`** (`PRD_R4.md:1819`,
   * `R4-F3` regla 8): `CAA2` lo había cerrado porque borrar un periodo con
   * métricas ya reportadas cambia números ya reportados, y R4 lo pide igual con
   * la confirmación que `R4-F3-CA-71` fija. No es una ruta que esta etapa
   * inventa: es la que el contrato ya declara.
   */
  readonly borrarSemana: (nombre: string) => Promise<void>
  // ⛔ RASTRO `ADR-0082` · ⟦ADR-0082 · 05/10/2026 · R4-UIM1⟧ — `descargarCsv` SE
  // COMENTA, NO SE BORRA. Era UNA descarga que NAVEGABA, y las dos cosas cambian
  // con `R4-UIM1` (A6 / `ADR-0386`, F3 RT 5 + D8). Lo que decía:
  //
  //   /**
  //    * `GET metricas/descarga`. NAVEGA; no devuelve nada y no hay nada que
  //    * esperar: el archivo lo arma `modules/analytics` y lo sirve `admin-web`.
  //    */
  //   readonly descargarCsv: () => void
  //
  // ⛔ Por qué deja de servir, medido: con `window.location.assign` **el
  // JavaScript no se entera de si el servidor contestó un 500 ni de cuándo bajó
  // el archivo**, así que `R4-F3-CA-88` («muestra "No se pudo descargar el CSV.
  // Intenta de nuevo." y no baja ningún archivo») y `R4-F3-CA-89` (el botón
  // «Descargando…» hasta que baja) **son imposibles de cumplir**. Y simular
  // «Descargando…» con un temporizador (Q5 (c)) mentiría sobre los dos.
  /**
   * ⭐ `R4-UIM1` · A6 / `ADR-0386` · **`GET metricas/descarga` — EL CSV DE
   * RESULTADOS, CON LA PRIMITIVA DE `R4-UI0`.**
   *
   * Devuelve una `Promise` que **resuelve cuando el archivo bajó** y **rechaza si
   * la descarga falló**, que es lo único que le permite a la pantalla decir
   * «Descargando…» y mostrar su aviso. Lo hace `descargarArchivo`
   * (`src/descargar-archivo.ts`): `fetch` con `credentials: "include"` → `!ok`
   * lanza → `Blob` → nombre de `Content-Disposition` → `<a download>`.
   *
   * ⛔ **El nombre lo pone el SERVIDOR y no hay nombre de respaldo.** `admin-web`
   * lo arma `metricas_resultados_<instancia>_<brazo>_<dd-mm-aaaa>_<hhmm>.csv`
   * (`PRD_R4.md:1731`) y lo manda en `Content-Disposition`; si no llega, la
   * descarga va por el camino de error. ⭐ Y la cabecera **cruza el origen**:
   * `apps/admin-web/src/cors.ts:134` emite
   * `Access-Control-Expose-Headers: Content-Disposition` (medido el 05/10/2026).
   * Sin ese `Expose-Headers` un `fetch` de otro origen no puede LEER la
   * cabecera aunque el servidor la mande, y todas las descargas caerían por el
   * camino de error.
   *
   * ⛔ **No lleva NADA colgado**: ni brazo, ni página, ni filtro. El brazo lo
   * resuelve `admin-web` de la sesión (`ADR-0006`) y el archivo es completo por
   * construcción (`PRD_R4.md:1731`: *«todos los periodos del brazo, en todas las
   * páginas»*).
   */
  readonly descargarCsvDeResultados: () => Promise<void>
  /**
   * ⭐ `R4-UIM1` · F3 RT 5 + D8 · **`GET errores/descarga` — LA SEGUNDA
   * DESCARGA.**
   *
   * La ruta y su código propio son del contrato, no de esta etapa:
   * `contracts/analytics-api/http.ts:450` declara
   * `ErroresDescargaPath = "errores/descarga"` y `:184` le da su desenlace
   * propio, `errorsCsvServed: 200` — *«un `200` PROPIO y no reusa `csvServed`»*.
   *
   * ⛔ **Es OTRO recurso y OTRA acción, no un parámetro de la primera.** Un
   * `?tipo=errores` sobre `metricas/descarga` habría sido una ruta que el
   * contrato no declara, y además dejaría las dos descargas compartiendo la fila
   * de auditoría: `PRD_R4.md:1755` pide «Descargó el CSV de errores de Métricas»
   * distinto de «Descargó el CSV de resultados de Métricas».
   */
  readonly descargarCsvDeErrores: () => Promise<void>
}

/** Error del borde con la forma que el resto de la app ya usa (`HttpError`). */
function errorDelBorde(mensaje: string, statusCode: number): Error & { statusCode: number } {
  return Object.assign(new Error(mensaje), { statusCode })
}

/**
 * El acuerdo de cable de las métricas, todo junto y en un solo lugar.
 *
 * Los valores literales repiten los del contrato; los tipos de path son del
 * contrato, así que una divergencia rompe el typecheck y no pasa desapercibida.
 */
const RUTA_METRICAS: MetricasPath = "metricas"
const RUTA_ERRORES: ErroresPath = "errores"
const RUTA_SEMANAS: SemanasPath = "semanas"
/**
 * `AW13` — la quinta ruta, la descarga del CSV (`P0-2`).
 *
 * Se escribe ENTERA como literal y se anota con `AnalyticsDescargaPath`, que es
 * la forma que `contracts/analytics-api/http.ts` pide para ella: sin la
 * anotación ésta sería la única de las cinco que puede quedar mal escrita en
 * producción sin que ningún build se caiga —no la recorre ningún `fetch` cuyo
 * tipo la ate—, y el día que el contrato le cambie el segmento el `404` se lee
 * como «la exportación está rota».
 */
const RUTA_DESCARGA: AnalyticsDescargaPath = "metricas/descarga"
/**
 * ⭐ `R4-UIM1` · **LA SEXTA RUTA: la segunda descarga** (F3 RT 5 + D8).
 *
 * Mismo criterio que `RUTA_DESCARGA`: se escribe entera como literal y se anota
 * con `ErroresDescargaPath`, que es la forma que
 * `contracts/analytics-api/http.ts:450` le da. Sin la anotación sería la única
 * que puede quedar mal escrita en producción sin que ningún build se caiga, y el
 * `404` se leería como «el CSV de errores está roto».
 */
const RUTA_DESCARGA_DE_ERRORES: ErroresDescargaPath = "errores/descarga"
const PARAMETRO_PAGINA = "pagina"
const PARAMETRO_TAMANO = "tamano"

/**
 * ⭐ `R4-UIM1` · **LOS DOS CUERPOS DEL `409`, ATADOS AL CONTRATO.**
 *
 * `contracts/analytics-api/http.ts:239` y `:247` exportan los dos textos como
 * CONSTANTES, y `:230-237` deja escrito por qué son DOS y no uno: *«la pantalla
 * tiene que poder distinguir cuál de las dos restricciones de unicidad se
 * rompió. Un solo "conflicto" la deja mostrando el aviso equivocado.»*
 *
 * ⛔ **Se anotan contra el tipo del contrato y no se importan como valor**, igual
 * que las seis rutas de arriba: `typeof import(...)` lo borra `esbuild`, así que
 * esto no mete `contracts/` en el bundle (`vite.config.ts:10-15`) y el día que
 * el contrato cambie uno de los dos textos **este archivo no compila**.
 *
 * ⚠️ Éstos son los textos del CUERPO, no los de PANTALLA. Los de pantalla
 * —«Ya hay un periodo con ese Item en este brazo.» y «… con esa Posición …»—
 * los fija `PRD_R4.md:1619` y los dibuja `pantalla.tsx`; el contrato lo dice
 * explícitamente: *«Dueño del texto VISIBLE: `R4-UIM1`; acá se fija el del
 * CUERPO.»*
 */
type CuerpoDelConflictoDeItem =
  typeof import("@contracts/analytics-api/http.ts").ANALYTICS_CONFLICT_ITEM
type CuerpoDelConflictoDePosicion =
  typeof import("@contracts/analytics-api/http.ts").ANALYTICS_CONFLICT_POSICION
export const CUERPO_CONFLICTO_ITEM: CuerpoDelConflictoDeItem = "El Item ya existe en este brazo."
export const CUERPO_CONFLICTO_POSICION: CuerpoDelConflictoDePosicion =
  "La Posición ya está ocupada en este brazo."

/** `semanas/{nombre}` — el `nombre` va percent-encoded, como fija `SemanaPath`. */
function rutaSemana(nombre: string): SemanaPath {
  return `${RUTA_SEMANAS}/${encodeURIComponent(nombre)}`
}

function esObjeto(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === "object" && valor !== null && !Array.isArray(valor)
}

function esEnteroNoNegativo(valor: unknown): valor is number {
  return typeof valor === "number" && Number.isInteger(valor) && valor >= 0
}

/**
 * Comprueba en runtime lo que el tipo promete y `response.json()` no puede saber.
 *
 * ⭐⭐ ⟦los-dias-no-se-convierten⟧ `R4-UIM1` · D3 / B13 · **`desde` y `hasta` son
 * DÍAS `aaaa-mm-dd` COMO TEXTO, Y ESTE BORDE NO LOS TOCA EN NINGUNA DIRECCIÓN.**
 *
 * `R4-CAN1` · `R4-F3` RT 1 (`PRD_R4.md:1814`) los cambió de `Date` a
 * `DiaDeChile` (`contracts/analytics/index.ts:123`), y el `typeof desde !==
 * "string"` de abajo es la comprobación entera: **no hay un `new Date(...)` en
 * este archivo, ni al leer ni al escribir** (ni un `.toISOString()`, ni un
 * `.getTime()`, ni un `Date.parse`).
 *
 * ⛔ `new Date("2026-07-06")` es medianoche UTC, así que en Chile —`UTC−4`—
 * cualquier formateo local da **`05-07-2026`**: el día ANTERIOR. Revivir el día
 * acá «para validarlo» lo correría una vez al leer y otra al escribir, y el
 * periodo se iría moviendo un día por cada ida y vuelta. Es el defecto que F3
 * ⛔3 midió y lo que `R4-F3-CA-14` (`PRD_R4.md:1839`) exige que no pase.
 *
 * ⚠️ **Y no se valida la FORMA del día acá**, sólo que sea texto. El `400` por un
 * `Desde` posterior a `Hasta` o una `Posición` menor que 1 lo da el servidor
 * (`R4-F3` RT 1), y la pantalla además no deja mandarlo (`R4-F3-CA-50`..`CA-54`).
 * Un tercer validador acá sería una tercera regla que puede divergir de las dos.
 */
function leerPeriodo(valor: unknown): SemanaRequestBody | null {
  if (!esObjeto(valor)) return null

  const nombre = valor["nombre"]
  if (typeof nombre !== "string") return null

  const desde = valor["desde"]
  if (typeof desde !== "string") return null

  const hasta = valor["hasta"]
  if (typeof hasta !== "string") return null

  const posicion = valor["posicion"]
  if (typeof posicion !== "number") return null

  return { nombre, desde, hasta, posicion }
}

/** Valida una página de métricas: `items` + `total` en la base. */
function leerPaginaDeMetricas(valor: unknown): MetricsResponseBody | null {
  if (!esObjeto(valor)) return null

  const itemsCrudo = valor["items"]
  if (!Array.isArray(itemsCrudo)) return null

  const items: Array<MetricsResponseBody["items"][number]> = []
  for (const item of itemsCrudo) {
    if (!esObjeto(item)) return null
    const periodo = leerPeriodo(item["periodo"])
    if (periodo === null) return null
    const metrics = item["metrics"]
    if (metrics === null || typeof metrics !== "object" || Array.isArray(metrics)) return null
    items.push({ periodo, metrics: metrics as MetricsResponseBody["items"][number]["metrics"] })
  }

  const total = valor["total"]
  if (!esEnteroNoNegativo(total)) return null

  return { items, total }
}

/** Valida una página de errores: `items` (con `errors` completo) + `total`. */
function leerPaginaDeErrores(valor: unknown): ErrorsResponseBody | null {
  if (!esObjeto(valor)) return null

  const itemsCrudo = valor["items"]
  if (!Array.isArray(itemsCrudo)) return null

  const items: Array<ErrorsResponseBody["items"][number]> = []
  for (const item of itemsCrudo) {
    if (!esObjeto(item)) return null
    const periodo = leerPeriodo(item["periodo"])
    if (periodo === null) return null

    const errorsCrudo = item["errors"]
    if (!Array.isArray(errorsCrudo)) return null
    const errors: Array<ErrorsResponseBody["items"][number]["errors"][number]> = []
    for (const error of errorsCrudo) {
      if (!esObjeto(error)) return null
      const code = error["code"]
      if (typeof code !== "string") return null
      const count = error["count"]
      if (!esEnteroNoNegativo(count)) return null
      errors.push({ code, count })
    }

    items.push({ periodo, errors })
  }

  const total = valor["total"]
  if (!esEnteroNoNegativo(total)) return null

  return { items, total }
}

/**
 * Arma el borde de métricas contra la `admin-web` configurada.
 *
 * Función y no módulo con estado, por lo mismo que `crearBordeExportacionHttp`:
 * la dirección llega en tiempo de ejecución (`configuracion.json`) y no hay
 * ningún momento de import en el que conocerla.
 */
export function crearBordeMetricasHttp(config: ConfiguracionInstancia): BordeMetricas {
  /**
   * ⭐ `AR5-m` · ⟦el-brazo-entra-por-ruta⟧ · **la raíz lleva el brazo adelante**, y
   * es una FUNCIÓN y no una constante porque el brazo sale de la dirección, que
   * cambia sin que este borde se vuelva a construir (`App.tsx` lo memoiza por
   * `config`). Ver `brazo-del-cable.ts`, que tiene la decisión del dueño citada.
   */
  const raiz = (): string => raizDelBrazo(config.adminWebUrl)

  /**
   * `credentials: "include"` en todos los pedidos, y ningún `Authorization`
   * armado acá. La sesión la maneja `admin-web` (Better Auth + MFA, `ADR-0006`)
   * en una cookie `HttpOnly` que el navegador envía solo.
   */
  const pedir = async (ruta: string, init: RequestInit): Promise<Response> => {
    let respuesta: Response
    try {
      respuesta = await fetch(`${raiz()}/${ruta}`, { ...init, credentials: "include" })
    } catch (causa) {
      throw Object.assign(errorDelBorde(`No se pudo hablar con el servidor (${config.adminWebUrl}).`, 0), {
        cause: causa,
      })
    }
    if (!respuesta.ok) {
      throw errorDelBorde(
        await mensajeDeError(respuesta, `El servidor respondió ${respuesta.status}.`),
        respuesta.status,
      )
    }
    return respuesta
  }

  const pedirPagina = async (
    ruta: string,
    query: PaginaQuery,
  ): Promise<{ readonly crudo: unknown }> => {
    const parametros = new URLSearchParams()
    parametros.set(PARAMETRO_PAGINA, String(query.page))
    parametros.set(PARAMETRO_TAMANO, String(query.pageSize))

    const respuesta = await pedir(`${ruta}?${parametros.toString()}`, {
      method: "GET",
      headers: { Accept: "application/json" },
    })

    return { crudo: await respuesta.json() }
  }

  return {
    /** `GET metricas?pagina=N&tamano=M`. El `total` es el de la base, no `items.length`. */
    async metricas(query: PaginaQuery): Promise<MetricsResponseBody> {
      const { crudo } = await pedirPagina(RUTA_METRICAS, query)
      const pagina = leerPaginaDeMetricas(crudo)
      if (pagina === null) {
        throw errorDelBorde("El servidor contestó la tabla de métricas en una forma que no se entiende.", 502)
      }
      return pagina
    },

    /** `GET errores?pagina=N&tamano=M`. Cada fila trae TODOS los códigos, esté o no el código visible. */
    async errores(query: PaginaQuery): Promise<ErrorsResponseBody> {
      const { crudo } = await pedirPagina(RUTA_ERRORES, query)
      const pagina = leerPaginaDeErrores(crudo)
      if (pagina === null) {
        throw errorDelBorde("El servidor contestó la tabla de errores en una forma que no se entiende.", 502)
      }
      return pagina
    },

    /**
     * `POST semanas`, cuerpo los cuatro campos de `Semana` con `Date` como texto.
     * Contesta `202` sin cuerpo: no se lee `.json()`. El resultado se ve en la
     * lectura (`metricas`), que es el recorrido del Definition of Done.
     */
    async cargarSemana(semana: SemanaRequestBody): Promise<void> {
      const cuerpo: SemanaRequestBody = semana
      await pedir(RUTA_SEMANAS, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(cuerpo),
      })
    },

    /**
     * `PUT semanas/{nombre}`, mismo cuerpo. `{nombre}` identifica el periodo —el
     * `Item` de la tabla— y va percent-encoded. Contesta `202`, `404` si no hay
     * periodo con ese `nombre` o `409` si la `Posición` choca (los tres los deja
     * pasar `pedir` como error con `statusCode`).
     */
    async editarSemana(semana: SemanaRequestBody): Promise<void> {
      const cuerpo: SemanaRequestBody = semana
      await pedir(rutaSemana(semana.nombre), {
        method: "PUT",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(cuerpo),
      })
    },

    /**
     * ⭐ `R4-UIM1` · `DELETE semanas/{nombre}`. Contesta `202` sin cuerpo.
     *
     * ⛔ **Sin cuerpo y sin `Content-Type`**: `SemanaDeleteRequest` es el
     * `nombre`, y el `nombre` ya va en la ruta. Un cuerpo en un `DELETE` es un
     * campo que el servidor no lee y que dos implementaciones de proxy tratan
     * distinto.
     */
    async borrarSemana(nombre: string): Promise<void> {
      await pedir(rutaSemana(nombre), {
        method: "DELETE",
        headers: { Accept: "application/json" },
      })
    },

    // ⛔ RASTRO `ADR-0082` · ⟦ADR-0082 · 05/10/2026 · R4-UIM1⟧ — LA NAVEGACIÓN SE
    // COMENTA, NO SE BORRA. El argumento era correcto para lo que la pantalla
    // podía hacer en F4 (no tenía estado de descarga que mostrar) y deja de
    // aplicar con `R4-F3-CA-88`/`CA-89`. Lo que decía, textual:
    //
    //   /**
    //    * `GET metricas/descarga`. Es una NAVEGACIÓN de documento y no un
    //    * `fetch`, igual que `descargar` de `borde-exportacion.ts`: `admin-web`
    //    * contesta con `Content-Disposition: attachment` y el navegador baja el
    //    * CSV sin sacar a la SPA de su página, con la cookie `HttpOnly` viajando
    //    * sola. La pantalla no serializa nada: no hay filas en memoria, no hay
    //    * blob, no hay archivo armado acá — y por eso no pasa por `pedir`, que
    //    * es el camino del `fetch`.
    //    *
    //    * ⛔ **No lleva NADA colgado**: ni brazo, ni instante, ni la página que
    //    * se está viendo, ni la lista de columnas visibles. El brazo lo resuelve
    //    * `admin-web` de la sesión y el instante lo pone el servidor; el archivo
    //    * es completo por construcción —se arma sobre todas las filas y todos
    //    * los códigos— y la lista visible es de la PRESENTACIÓN, otro alcance.
    //    * Un `?brazo=` acá no devolvería una fila del otro brazo: devolvería su
    //    * archivo entero, que ya salió del sistema.
    //    */
    //   descargarCsv: (): void => {
    //     window.location.assign(`${raiz()}/${RUTA_DESCARGA}`)
    //   },
    //
    // ⚠️ **Lo que el párrafo de arriba decía y SIGUE siendo cierto**: la descarga
    // no lleva nada colgado y el archivo es completo por construcción. Eso no
    // cambió. Lo que cambió es el MECANISMO —`fetch`+`Blob` en vez de navegar—
    // y por qué: una navegación no se puede esperar ni se puede ver fallar.

    /**
     * `GET metricas/descarga` con la primitiva de `R4-UI0`.
     *
     * ⛔ **No pasa por `pedir`, y es a propósito.** `pedir` lee el cuerpo como
     * texto para armar el mensaje de error y devuelve la `Response`; acá el
     * cuerpo es un CSV que tiene que llegar al disco como `Blob`, no como
     * string. `descargarArchivo` hace el `fetch` con `credentials: "include"`
     * —la misma cookie `HttpOnly`— y es la primitiva única que `ADR-0386` fija.
     *
     * ⚠️ El error que lanza `descargarArchivo` **no trae `statusCode`**: lo arma
     * con `new Error`. La pantalla no lo necesita —`PRD_R4.md:1753` le da UN
     * aviso para cualquier fallo de descarga, «No se pudo descargar el CSV.
     * Intenta de nuevo.»— así que no se envuelve para agregarle un campo que
     * nadie lee.
     */
    descargarCsvDeResultados: (): Promise<void> => descargarArchivo(`${raiz()}/${RUTA_DESCARGA}`),

    /** `GET errores/descarga` con la misma primitiva. F3 RT 5 + D8. */
    descargarCsvDeErrores: (): Promise<void> =>
      descargarArchivo(`${raiz()}/${RUTA_DESCARGA_DE_ERRORES}`),
  }
}
